/**
 * Walk it through, step 1: the page as people open it, every part clickable and
 * its words typeable where they stand. Clicking a part opens its conversation
 * beside it; "Change the whole page" opens the page's own. Every card is a plain
 * chat: the host says in words what should change. The address can be changed in
 * place.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LEARN_SLOT } from "../../../shared/page-slots";
import type { BoardState } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { useToast } from "../../components/ui/Toast";
import { asksIn, WHOLE_PAGE, type PageStudio } from "./pageStudio";
import { FOLD, foldDoc } from "./foldUnits";
import { sizeFrame, slotIndex, usePartBefore, WALK_STYLE } from "./pageFrame";
import { useCardPlace } from "./placeCard";
import { ViewSeg } from "./ViewSeg";
import { WalkChat } from "./WalkChat";

/** The chapters part, which the board save refills from the chapters card. */
const CHAPTERS = "chapters";
/**
 * How the page is drawn before it is scaled to fit the step: the width of the screen it stands for, the first screen a
 * guest sees (the line across it), the browser's bar, the frame's edges, and the most it is scaled to (a phone stays
 * a phone's size).
 */
const VIEWS = {
  desktop: { width: 1440, fold: FOLD.desktop, bar: 44, edge: 2, most: 1 },
  phone: { width: 390, fold: FOLD.phone, bar: 0, edge: 20, most: 0.86 },
} as const;

/** Scales a fixed-width page down to fit the step, and reports the scale. */
function useFit(el: HTMLDivElement | null, width: number, most: number) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    if (!el) return;
    const fit = () => setScale(el.clientWidth ? Math.min(most, el.clientWidth / width) : 1);
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [el, width, most]);
  return scale;
}

interface WalkPageProps {
  studio: PageStudio;
  live: boolean;
  phone: boolean;
  onPhone: (phone: boolean) => void;
  /** "maya-torres.flashroom.ai" style: the page's address before its own part. */
  base: string;
  slug: string;
  onSlug: (slug: string) => void;
  /** The part whose card is open, by its section name; "page" for the whole page. */
  open: string | null;
  onOpen: (section: string | null) => void;
  /** A chapter's name typed over on the page: it is the board's chapters card that holds it. */
  onChapterName: (index: number, name: string) => void;
  /** Split or join chapters, on the talk. */
  onChapters: () => void;
  /** Words typed in place: their way back, for the bar's Undo. */
  onTyped?: (undo: (now: BoardState) => void) => () => void;
}

export function WalkPage({ studio, live, phone, onPhone, base, slug, onSlug, open, onOpen, onChapterName, onChapters, onTyped }: WalkPageProps) {
  const toast = useToast();
  const frame = useRef<HTMLIFrameElement>(null);
  const view = phone ? VIEWS.phone : VIEWS.desktop;
  // Why the step in state: it mounts only once the page is drawn, after the first draw, and a ref read at the first
  // draw never fitted it.
  const [stage, setStage] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number>(view.fold);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(slug);
  /** Where the click that opened the card landed inside its part, and whether the card takes the caret. */
  const [opened, setOpened] = useState<{ at: { dx: number; dy: number } | null; focus: boolean }>({ at: null, focus: true });
  const [peek, setPeek] = useState(false);
  // Holding Before on the whole page's latest change draws the page as it was, its frame marked.
  const [peekPage, setPeekPage] = useState(false);
  const wholeBefore = peekPage ? studio.before(WHOLE_PAGE) : null;
  const shownDoc = useMemo(() => {
    const doc = wholeBefore !== null ? studio.docOf(wholeBefore) : studio.doc;
    return doc === null ? "" : foldDoc(doc, view.fold);
  }, [wholeBefore, studio, view.fold]);
  const fullW = view.width + view.edge;
  const scale = useFit(stage, fullW, view.most);
  // Why a ref: the page's own listeners are wired once per draw and must reach the latest studio.
  const words = useRef(studio.words);
  words.current = studio.words;
  const openNow = useRef(open);
  openNow.current = open;
  const chapterName = useRef(onChapterName);
  chapterName.current = onChapterName;
  const typed = useRef(onTyped);
  typed.current = onTyped;
  /** How much the page is scaled down on the step: a click inside it is measured at this size. */
  const shrink = useRef(1);
  shrink.current = scale;
  const link = `${base}/${slug}`;
  const label = (name: string) => (name === WHOLE_PAGE ? "The whole page" : studio.sections.find((s) => s.name === name)?.label ?? "This part");

  /** The open part on the step: the part inside the page, at the size the page is drawn, or the whole page's button. */
  const partBox = () => {
    if (!open) return null;
    if (open === WHOLE_PAGE) return stage?.querySelector(".bw-whole")?.getBoundingClientRect() ?? null;
    const el = frame.current?.contentDocument?.querySelector<HTMLElement>(`[data-section="${CSS.escape(open)}"]`);
    const f = frame.current?.getBoundingClientRect();
    if (!el || !f) return null;
    const r = el.getBoundingClientRect();
    const k = shrink.current;
    return { left: f.left + r.left * k, top: f.top + r.top * k, width: r.width * k, height: r.height * k };
  };
  const cards = useCardPlace({
    key: open,
    whole: open === WHOLE_PAGE,
    at: opened.at,
    part: partBox,
    deps: [height, scale, studio.html, peek, peekPage, phone],
  });

  // Dress the page every time it is drawn: the walk's own style, every part clickable, the badges.
  const dress = useCallback(() => {
    const d = frame.current?.contentDocument;
    if (!d?.body) return;
    if (!d.getElementById("bw-style")) {
      const style = d.createElement("style");
      style.id = "bw-style";
      style.textContent = WALK_STYLE;
      d.head.appendChild(style);
    }
    // Why: keys pressed with the caret inside the page never reach the window, so Esc is heard here as well.
    if (!d.documentElement.dataset.bwEsc) {
      d.documentElement.dataset.bwEsc = "1";
      d.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && openNow.current) onOpen(null);
      });
    }
    d.querySelectorAll("form").forEach((f) => f.addEventListener("submit", (e) => e.preventDefault()));
    d.querySelectorAll("a[href]").forEach((a) => a.addEventListener("click", (e) => e.preventDefault()));
    const parts = [...d.querySelectorAll<HTMLElement>("[data-section]")].filter((el) => !el.parentElement?.closest("[data-section]"));
    for (const el of parts) {
      const name = el.dataset.section ?? "";
      const n = asksIn(studio.threads[name]);
      if (n) el.dataset.bwn = String(n);
      else delete el.dataset.bwn;
      el.classList.toggle("bw-sel", name === open);
      el.classList.toggle("bw-work", name === studio.working);
      el.classList.toggle("bw-landed", name === studio.landed);
      // Every part's words are typed over in place, the way the page shows them; not while Before shows the page as
      // it was, nor while Flash is changing that part (or the whole page).
      const typeable = !peek && !peekPage && studio.working !== name && studio.working !== WHOLE_PAGE;
      for (const slot of el.querySelectorAll<HTMLElement>("[data-slot]")) {
        if (typeable && slot.tagName !== "IMG") {
          slot.setAttribute("contenteditable", "plaintext-only");
          slot.setAttribute("spellcheck", "false");
        } else slot.removeAttribute("contenteditable");
      }
      if (el.dataset.bwWired) continue;
      el.dataset.bwWired = "1";
      el.addEventListener("click", (e) => {
        // A click into the words is for typing: it opens the part's card, never closes it, and leaves the caret there.
        const inText = !!(e.target as HTMLElement).closest("[data-slot][contenteditable]");
        if (!inText) {
          e.preventDefault();
          e.stopPropagation();
        }
        if (openNow.current === name) {
          if (!inText) onOpen(null);
          return;
        }
        const r = el.getBoundingClientRect();
        const k = shrink.current;
        setOpened({ at: { dx: (e.clientX - r.left) * k, dy: (e.clientY - r.top) * k }, focus: !inText });
        onOpen(name);
      });
      el.addEventListener(
        "focusin",
        (e) => {
          const t = e.target as HTMLElement;
          if (t.matches("[data-slot][contenteditable]")) t.dataset.was = t.textContent ?? "";
        },
        true,
      );
      el.addEventListener(
        "focusout",
        (e) => {
          const t = e.target as HTMLElement;
          if (!t.matches("[data-slot][contenteditable]")) return;
          const now = (t.textContent ?? "").replace(/\s*\n\s*/g, " ").trim();
          const was = (t.dataset.was ?? "").trim();
          if (!now) {
            t.textContent = t.dataset.was ?? "";
            // A line of "What you'll learn" left empty is taken out, as the prototype does; the last one stays.
            if (was && t.dataset.slot === LEARN_SLOT && el.querySelectorAll(`[data-slot="${LEARN_SLOT}"]`).length > 1) {
              const index = slotIndex(el, t);
              void words.current(name, LEARN_SLOT, index, "", "out");
              const back = typed.current?.(() => void words.current(name, LEARN_SLOT, index, was, "put"));
              toast({ text: "Line taken out.", icon: "check", action: back ? { label: "Undo", onClick: back } : undefined });
              return;
            }
            toast({ text: t.dataset.slot === "title" ? "A room needs a name." : "That can’t be empty.", icon: "info" });
            return;
          }
          if (now === was) return;
          const slot = t.dataset.slot ?? "";
          const index = slotIndex(el, t);
          // Why: the page's chapters are refilled from the board's card on every save, so a name typed there goes to the card.
          if (name === CHAPTERS && slot === "chapter") {
            chapterName.current(index, now);
            typed.current?.(() => chapterName.current(index, was));
          } else {
            void words.current(name, slot, index, now);
            typed.current?.(() => void words.current(name, slot, index, was));
          }
        },
        true,
      );
      el.addEventListener("keydown", (e) => {
        const t = e.target as HTMLElement;
        if ((e.key === "Enter" || e.key === "Escape") && t.matches("[data-slot][contenteditable]")) {
          e.preventDefault();
          t.blur();
          // Esc also puts the card away, as it does outside the page.
          if (e.key === "Escape") onOpen(null);
        }
      });
    }
    d.body.classList.toggle("bw-busy", !!studio.working && studio.working !== WHOLE_PAGE);
    if (frame.current) sizeFrame(frame.current, view.fold, setHeight);
    // The click handler above was bound to the first render's `onOpen`, which is stable.
  }, [onOpen, open, peek, peekPage, studio.landed, studio.threads, studio.working, toast, view.fold]);

  useEffect(() => {
    dress();
  }, [dress]);

  const { clearError } = studio;
  useEffect(() => clearError(), [open, clearError]);

  usePartBefore(frame, open, studio, peek, dress);

  const saveSlug = () => {
    const v = draft
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60);
    if (!v) {
      toast({ text: "Add an address first", icon: "info" });
      return;
    }
    setEditing(false);
    if (v !== slug) {
      onSlug(v);
      toast("Address saved");
    }
  };

  const wholeCount = asksIn(studio.threads[WHOLE_PAGE]);
  const thread = open ? (studio.threads[open] ?? []) : [];

  if (studio.doc === null) {
    return <div className="card nbw nbload" aria-busy="true" aria-label="Opening the page" />;
  }
  return (
    <div className="bw-pagestep" ref={setStage}>
      <div className="bw-ptools">
        <button
          type="button"
          className={cx("bw-whole", open === WHOLE_PAGE && "bw-sel")}
          aria-expanded={open === WHOLE_PAGE}
          onClick={() => {
            setOpened({ at: null, focus: true });
            onOpen(open === WHOLE_PAGE ? null : WHOLE_PAGE);
          }}
        >
          <Icon name="palette" />
          <span>
            <b>Change the whole page</b>
          </span>
          {wholeCount ? (
            <span className="bw-wn" aria-label={`${wholeCount} in its chat`}>
              {wholeCount}
            </span>
          ) : null}
          <Icon name="down" />
        </button>
        <div className="bw-addrw">
          {editing ? (
            <span className="bw-addr bw-addr-ed">
              <Icon name="link" />
              <span className="mono">{base}/</span>
              <input dir="auto"
                className="inp mono"
                value={draft}
                autoFocus
                aria-label="The page’s address"
                spellCheck={false}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveSlug();
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    setEditing(false);
                    setDraft(slug);
                  }
                }}
              />
              <button type="button" className="ibtn sm bw-slugok" title="Save" aria-label="Save the address" onClick={saveSlug}>
                <Icon name="check" />
              </button>
              <button type="button" className="ibtn sm" title="Cancel" aria-label="Cancel" onClick={() => setEditing(false)}>
                <Icon name="x" />
              </button>
            </span>
          ) : (
            <span className="bw-addr">
              <Icon name="link" />
              <button
                type="button"
                className="bw-slugbtn"
                title="Change the address"
                aria-label={`Change the address ${link}`}
                onClick={() => {
                  setDraft(slug);
                  setEditing(true);
                }}
              >
                <span className="mono">
                  {base}/<b>{slug}</b>
                </span>
                <Icon name="pencil" />
              </button>
              <button
                type="button"
                className="ibtn sm"
                title="Copy the address"
                aria-label="Copy the address"
                onClick={() => {
                  void navigator.clipboard?.writeText(`https://${link}`).catch(() => undefined);
                  toast({ text: "Copied", icon: "copy" });
                }}
              >
                <Icon name="copy" />
              </button>
            </span>
          )}
        </div>
        <ViewSeg phone={phone} onPhone={onPhone} />
      </div>
      <div className="bw-fitbox" style={{ width: fullW * scale, height: (height + view.bar + view.edge) * scale }}>
        <div className="bw-fold" style={{ top: (view.bar + view.fold + view.edge / 2) * scale }} aria-hidden="true">
          <span>What a guest sees first</span>
        </div>
        <div className="bw-z" style={{ width: fullW, transform: `scale(${scale})` }}>
          {phone ? (
            <div className={cx("bw-phone bw-pgphone", open === WHOLE_PAGE && "bw-sel", peekPage && "bw-pv", studio.working === WHOLE_PAGE && "bw-working", studio.landed === WHOLE_PAGE && "bw-landed")}>
              <div className="bw-phone-in">
                <iframe ref={frame} className="bw-pframe" title="The page on a phone" srcDoc={shownDoc} scrolling="no" onLoad={dress} style={{ height }} />
              </div>
            </div>
          ) : (
            <div className={cx("bw-browser", open === WHOLE_PAGE && "bw-sel", peekPage && "bw-pv", studio.working === WHOLE_PAGE && "bw-working", studio.landed === WHOLE_PAGE && "bw-landed")}>
              <div className="browser">
                <div className="bb">
                  <i />
                  <i />
                  <i />
                  <span className="url">
                    <Icon name="lock" />
                    {link}
                  </span>
                </div>
                <iframe ref={frame} className="bw-pframe" title="The page" srcDoc={shownDoc} scrolling="no" onLoad={dress} style={{ height }} />
              </div>
            </div>
          )}
        </div>
      </div>
      {open ? (
        <div className="bw-cards" ref={cards}>
          <WalkChat
            key={open}
            title={label(open)}
            thread={thread}
            busy={studio.working === open}
            onAsk={(text, files) => studio.ask(open, text, files)}
            onBack={(id) => studio.back(open, id)}
            onPeek={open === WHOLE_PAGE ? setPeekPage : setPeek}
            onClose={() => onOpen(null)}
            placeholder={open === WHOLE_PAGE ? "What should change on the page?" : open === CHAPTERS ? "Click a chapter’s name on the page to rename it." : "What should change here?"}
            acts={open === CHAPTERS && !live ? [{ label: "Split or join chapters", icon: "scissors", onClick: onChapters }] : undefined}
            error={studio.errorIn(open) || undefined}
            focus={opened.focus}
          />
        </div>
      ) : null}
    </div>
  );
}
